import { Injectable } from '@angular/core';
import { ToastService, MonPromptService, DialogService } from '@monsido/angular-shared-components';
import { TranslateService } from 'app/services/translate/translate.service';
import { CustomerInterface } from '@monsido/modules/models/api/interfaces/customer.interface';
import { Domain } from '@monsido/modules/models/api/domain';
import { Observable, Subscription } from 'rxjs';
import { FormImportWizardComponent } from '../forms/import-wizard/import-wizard.component';
import { DomainActionProgress } from '../types/domain-management.types';

@Injectable({
    providedIn: 'root',
})
export class DomainManagementSharedService {
    constructor (
        private toastService: ToastService,
        private monPromptService: MonPromptService,
        private translateService: TranslateService,
        private dialogService: DialogService,
    ) {}

    /**
     * Sanitize customer name for filename
     */
    sanitizeCustomerName (customer: CustomerInterface): string {
        const customerName = customer?.name || customer?.id?.toString() || 'customer';
        return customerName.replace(/[^a-zA-Z0-9-_]/g, '_');
    }

    /**
     * Show confirmation dialog with translated message
     */
    async showConfirmation (message: string, title: string): Promise<boolean> {
        try {
            const confirmed = await this.monPromptService.confirm(
                this.translateService.getString(message),
                { title: this.translateService.getString(title) },
            );
            return confirmed !== null && confirmed !== undefined;
        } catch (error) {
            return false;
        }
    }

    /**
     * Show success toast message
     */
    showSuccessMessage (message: string, params?: Record<string, string | number>): void {
        this.toastService.success(
            this.translateService.getString(message, params),
        );
    }

    /**
     * Show error toast message
     */
    showErrorMessage (message: string, params?: Record<string, string | number>): void {
        this.toastService.error(
            this.translateService.getString(message, params),
        );
    }

    /**
     * Show warning toast message
     */
    showWarningMessage (message: string, params?: Record<string, string | number>): void {
        this.toastService.warning(
            this.translateService.getString(message, params),
        );
    }

    /**
     * Validate domains selection
     */
    validateDomainsSelection (domains: Domain[]): boolean {
        if (domains.length === 0) {
            this.showWarningMessage('No domains selected for export. Please select domains first.');
            return false;
        }
        return true;
    }

    /**
     * Validate customer information
     */
    validateCustomer (customer: CustomerInterface): boolean {
        if (!customer?.id) {
            this.showErrorMessage('Cannot export domains: customer information not available');
            return false;
        }
        return true;
    }

    /**
     * Format domain list for confirmation dialogs
     */
    formatDomainList (domains: Domain[]): string {
        return domains.map(d => `• ${d.title || d.url}`).join('\n');
    }

    /**
     * Create export confirmation message for selected domains
     */
    createSelectedExportConfirmation (domains: Domain[]): string {
        const domainList = this.formatDomainList(domains);
        return this.translateService.getString(
            'Are you sure you want to export [[count]] selected domains?\n\nDomains to export:\n[[domains]]',
            {
                count: domains.length,
                domains: domainList,
            },
        );
    }

    /**
     * Create export confirmation message for all domains
     */
    createAllExportConfirmation (): string {
        return this.translateService.getString(
            'Are you sure you want to export ALL domains for this customer?' +
            '\n\nThis may download a large file and take several minutes depending on the number of domains.',
        );
    }

    /**
     * Create progress tracker observable wrapper
     */
    createProgressTracker (
        sourceProgress$: Observable<unknown>,
        onUpdate: (progress: DomainActionProgress) => void,
    ): { subscribe: () => Subscription; unsubscribe: () => void } {
        let subscription: Subscription | undefined;

        return {
            subscribe: (): Subscription => {
                subscription = sourceProgress$.subscribe((progress: { current: number; total: number; message: string }) => {
                    onUpdate({
                        current: progress.current,
                        total: progress.total,
                        message: progress.message,
                        isActive: true,
                    });
                });
                return subscription;
            },
            unsubscribe: (): void => {
                if (subscription) {
                    subscription.unsubscribe();
                }
            },
        };
    }

    openJsonImportWizard (
        customer: CustomerInterface,
        monIsBackendAdmin: boolean,
        onStarted: () => void,
        onCompleted: () => void,
        onFailed: (error: string) => void,
    ): void {
        onStarted();

        const dialogRef = this.dialogService.open(FormImportWizardComponent, {
            classes: 'mon-dialog-size-lg',
            defaultWrapper: false,
            formConfirmPromptParentSelector: '#common-dialog-wrapper',
            cb: async (refreshNeeded: boolean) => {
                if (refreshNeeded) {
                    onCompleted();
                    this.showSuccessMessage('Domains imported successfully');
                } else {
                    onFailed('Import was cancelled or failed');
                }
            },
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (dialogRef.componentInstance as any).customer = customer;
        dialogRef.componentInstance.monIsBackendAdmin = monIsBackendAdmin;
    }
}
